/**
 * The map's own question: what is the point under the pin called?
 *
 * THE OPPOSITE DIRECTION FROM PLACE SEARCH. `placeSearch.ts` turns a name the
 * customer typed into a point. This turns a point they dragged to into a name
 * — and dragging is how most customers answer, because the landmark they
 * searched for is rarely their own door.
 *
 * Answered by the server, not by Google in the browser, for one reason that
 * matters more than the others: checkout refuses an order whose saved PIN code
 * differs from the PIN code the delivery was priced against. Whatever fills
 * that field has to fill it once, from one source, for both. The server is
 * that source; this is how the screen asks it.
 */
import { api } from './client';
import { queryKeys, useApiQuery } from './queryClient';
import type { ReverseGeocodeOut } from './types';

export const GEO_REVERSE_PATH = '/api/v1/geo/reverse';

export function reverseGeocode(
  latitude: number,
  longitude: number,
  signal?: AbortSignal,
): Promise<ReverseGeocodeOut> {
  return api.post<ReverseGeocodeOut>(
    GEO_REVERSE_PATH,
    { latitude, longitude },
    signal ? { signal } : undefined,
  );
}

/** How far a pin has to move before it is a different place. See the server's cache. */
const KEY_PRECISION = 4;

/**
 * The name of a point, as a query.
 *
 * Keyed on the ROUNDED coordinate, to four decimals — about eleven metres —
 * because that is the server's own cache granularity: two settles inside it
 * are one question, and a query key that changed in the sixth decimal would
 * refetch (and the server would answer from cache) on every nudge of the map.
 *
 * `enabled` is false with no point: the screen before the pin lands has
 * nothing to ask about.
 */
export function useReverseGeocode(point: { lat: number; lng: number } | null) {
  const lat = point ? Number(point.lat.toFixed(KEY_PRECISION)) : null;
  const lng = point ? Number(point.lng.toFixed(KEY_PRECISION)) : null;
  return useApiQuery<ReverseGeocodeOut, ReturnType<typeof queryKeys.reverseGeocode>>({
    queryKey: queryKeys.reverseGeocode(lat, lng),
    queryFn: ({ signal }) => reverseGeocode(point!.lat, point!.lng, signal),
    enabled: lat !== null && lng !== null,
    // An address does not move. Once named, a point stays named for the
    // life of the page — dragging away and back costs nothing.
    staleTime: Infinity,
    gcTime: 30 * 60_000,
  });
}
